import React, { useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Link } from 'react-router-dom';
import { Bars3Icon, ClipboardDocumentListIcon, ChartBarIcon, ArrowUpTrayIcon, SparklesIcon, RocketLaunchIcon, LockClosedIcon, MapIcon, CakeIcon, Cog6ToothIcon, ShieldCheckIcon, FunnelIcon } from '@heroicons/react/24/solid';
import { useSubscription, useFeature } from '../contexts/SubscriptionContext';
import { useModal } from '../services/ModalService';
import UpgradeModal from './UpgradeModal';
import './HamburgerMenu.css';

const HamburgerMenu = ({ onEditPreferences, onUpdatePlan, onShowStats, onImportExport, onShowInferences, onShowStrategy, onManageFoods, onShowDojo, hasDojo, onActivityFilters, activityFilterSummary, onShowProfile, profilePortrait }) => {
  // onUpdatePlan is the legacy plan editor fallback for non-AI users
  const [isOpen, setIsOpen] = React.useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const { subscription, prices, openCheckout, paddleReady, openPortal, gatingEnabled } = useSubscription();
  const { hasAccess: hasAiInference, openUpgrade } = useFeature('ai_inference');
  const modalService = useModal();

  // Open upgrade modal with subscription context passed as props
  const handleUpgrade = () => {
    modalService.open(UpgradeModal, {
      subscriptionProps: { prices, openCheckout, subscription, paddleReady, openPortal }
    });
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleClickOutside = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousPadding = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    document.body.style.overflow = 'hidden';
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPadding;
    };
  }, [isOpen]);

  return (
    <div className="hamburger-menu" ref={menuRef}>
      <button type="button" ref={triggerRef} className="menu-icon" aria-label="Open menu" aria-expanded={isOpen} onClick={toggleMenu}><Bars3Icon /></button>
      {isOpen && (
        <>
          <div className="menu-overlay" onClick={toggleMenu}></div>
          <div className="menu-dropdown">
            {onShowProfile && (
              <button type="button" className="menu-item menu-profile-item" aria-haspopup="dialog" onClick={() => {
                // Release the menu lock before the modal captures body styles.
                flushSync(() => setIsOpen(false));
                triggerRef.current?.focus({ preventScroll: true });
                onShowProfile();
              }}>
                {profilePortrait}
                <span>Profile &amp; Account</span>
              </button>
            )}
            {onActivityFilters && (
              <button type="button" className="menu-item" aria-haspopup="dialog" onClick={() => {
                // Release the menu's scroll lock before the dialog acquires it.
                flushSync(() => setIsOpen(false));
                onActivityFilters(triggerRef.current);
              }}>
                <FunnelIcon className="menu-item-icon" />
                <span className="menu-filter-label">Activity filters<small>{activityFilterSummary || 'Displaying all activities'}</small></span>
              </button>
            )}
            <button
              onClick={() => {
                onEditPreferences();
                setIsOpen(false);
              }}
              className="menu-item"
            >
              <Cog6ToothIcon className="menu-item-icon" />
              Preferences
            </button>
            {onShowDojo && (
              <button onClick={() => { onShowDojo(); setIsOpen(false); }} className="menu-item">
                <ShieldCheckIcon className="menu-item-icon" />
                Dojo
              </button>
            )}

            {!hasDojo && (onShowStrategy ? (
              <button
                onClick={() => {
                  if (hasAiInference) {
                    onShowStrategy();
                  } else if (onUpdatePlan) {
                    onUpdatePlan();
                  } else {
                    openUpgrade();
                  }
                  setIsOpen(false);
                }}
                className="menu-item"
              >
                <MapIcon className="menu-item-icon" />
                Strategy & Plan
                {!hasAiInference && <span className="pro-badge-small">PRO</span>}
              </button>
            ) : (
              <button
                onClick={() => {
                  if (onUpdatePlan) onUpdatePlan();
                  setIsOpen(false);
                }}
                className="menu-item"
              >
                <ClipboardDocumentListIcon className="menu-item-icon" />
                Workout Plan
              </button>
            ))}
            <button
              onClick={() => {
                onShowStats();
                setIsOpen(false);
              }}
              className="menu-item"
            >
              <ChartBarIcon className="menu-item-icon" />
              Stats
            </button>
            {onShowInferences && (
              <button
                onClick={() => {
                  if (hasAiInference) {
                    onShowInferences();
                  } else {
                    openUpgrade();
                  }
                  setIsOpen(false);
                }}
                className={`menu-item ${!hasAiInference ? 'menu-item-locked' : ''}`}
              >
                {hasAiInference ? (
                  <SparklesIcon className="menu-item-icon" />
                ) : (
                  <LockClosedIcon className="menu-item-icon menu-item-icon-locked" />
                )}
                Inferences
                {!hasAiInference && <span className="pro-badge-small">PRO</span>}
              </button>
            )}
            <button
              onClick={() => {
                if(onManageFoods) onManageFoods();
                setIsOpen(false);
              }}
              className="menu-item"
            >
              <CakeIcon className="menu-item-icon" />
              Foods &amp; Recipes
            </button>
            <button
              onClick={() => {
                onImportExport();
                setIsOpen(false);
              }}
              className="menu-item"
            >
              <ArrowUpTrayIcon className="menu-item-icon" />
              Import / Export
            </button>
            {/* Only show upgrade button if gating is enabled AND user is not pro */}
            {gatingEnabled && subscription?.tier !== 'pro' && (
              <button
                onClick={() => {
                  handleUpgrade();
                  setIsOpen(false);
                }}
                className="menu-item menu-item-upgrade"
              >
                <RocketLaunchIcon className="menu-item-icon" />
                Upgrade to Pro
              </button>
            )}
            {/* Legal links */}
            <div className="menu-legal-links">
              <Link to="/pricing" onClick={() => setIsOpen(false)}>Pricing</Link>
              <Link to="/terms-and-conditions" onClick={() => setIsOpen(false)}>Terms</Link>
              <Link to="/privacy" onClick={() => setIsOpen(false)}>Privacy</Link>
              <Link to="/refund" onClick={() => setIsOpen(false)}>Refunds</Link>
              <Link to="/support" onClick={() => setIsOpen(false)}>Support</Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HamburgerMenu;
